import type { Coordinates, DesignSite, EquipmentPerformanceProfile, ManualObstacle, ObstacleKind, ProjectViewState, SiteType } from '@freedom/shared';
import type { ProjectObjectRecord, ProjectWorkspaceObject } from './api';
import { defaultDisplaySettings, normalizeExplicitLinkLossDb, type DisplaySettings, type PtMpGroupModel, type ProductSelectionMode, type RadioLinkModel } from './radio-workspace';

export interface HydratedProjectWorkspace {
  sites: DesignSite[];
  obstacles: ManualObstacle[];
  linkEndpointIds: [string, string] | null;
  radioLinks: RadioLinkModel[];
  ptmpGroups: PtMpGroupModel[];
  displaySettings: DisplaySettings;
}

export interface HydrateProjectWorkspaceOptions {
  inferLegacyLink?: boolean;
}

export function hydrateProjectViewState(settings: Record<string, unknown> | undefined, viewport: Coordinates | null | undefined, fallback: ProjectViewState): ProjectViewState {
  const raw = typeof settings?.viewState === 'object' && settings.viewState !== null ? settings.viewState as Record<string, unknown> : {};
  const centerValue = typeof raw.center === 'object' && raw.center !== null ? raw.center as Record<string, unknown> : {};
  const lat = finiteNumber(centerValue.lat, viewport?.lat ?? fallback.center.lat);
  const lon = finiteNumber(centerValue.lon, viewport?.lon ?? fallback.center.lon);
  const zoom = finiteNumber(raw.zoom, fallback.zoom);
  const pitch = finiteNumber(raw.pitch, fallback.pitch);
  const bearing = finiteNumber(raw.bearing, fallback.bearing);
  return {
    center: { lat: Math.max(-90, Math.min(90, lat)), lon: Math.max(-180, Math.min(180, lon)) },
    zoom: Math.max(0, Math.min(24, zoom)),
    pitch: Math.max(0, Math.min(85, pitch)),
    bearing,
    activeLayers: Array.isArray(raw.activeLayers) ? raw.activeLayers.filter((layer): layer is string => typeof layer === 'string').slice(0, 20) : fallback.activeLayers,
    selectedObjectId: typeof raw.selectedObjectId === 'string' ? raw.selectedObjectId : null,
  };
}

function pointCoordinates(geometry: Record<string, unknown>): { lat: number; lon: number } | null {
  if (geometry.type !== 'Point' || !Array.isArray(geometry.coordinates)) return null;
  const lon = Number(geometry.coordinates[0]);
  const lat = Number(geometry.coordinates[1]);
  return Number.isFinite(lat) && Number.isFinite(lon) && Math.abs(lat) <= 90 && Math.abs(lon) <= 180 ? { lat, lon } : null;
}

function finiteNumber(value: unknown, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

// Match the link-analysis API's absolute validation envelope. The interactive control may
// use a narrower range, but persistence must not rewrite otherwise valid imported projects.
const MIN_PERSISTED_TX_POWER_DBM = -100;
const MAX_PERSISTED_TX_POWER_DBM = 60;

function persistedTxPowerDbm(value: unknown, fallback = 18): number {
  return Math.max(MIN_PERSISTED_TX_POWER_DBM, Math.min(MAX_PERSISTED_TX_POWER_DBM, finiteNumber(value, fallback)));
}

function selectionMode(value: unknown, fallback: ProductSelectionMode = 'auto'): ProductSelectionMode {
  return value === 'manual' ? 'manual' : value === 'auto' ? 'auto' : fallback;
}

function stringList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string' && item.length > 0) : [];
}

type SiteEquipmentSnapshot = Pick<DesignSite,
  | 'radioDeviceId'
  | 'radioMaxTxPowerDbm'
  | 'radioChannelWidthsMHz'
  | 'radioReceiverSensitivityDbm'
  | 'radioMaxThroughputMbps'
  | 'radioMaxRangeKm'
  | 'radioPolarization'
  | 'radioPerformanceProfiles'
  | 'antennaModelId'
  | 'antennaName'
>;

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? value as Record<string, unknown> : null;
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined;
}

function optionalNullableString(value: unknown): string | null | undefined {
  return value === null ? null : optionalString(value);
}

function optionalNullableNumber(value: unknown): number | null | undefined {
  return value === null ? null : typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function optionalNullableBoundedNumber(value: unknown, minimum: number, maximum: number): number | null | undefined {
  const parsed = optionalNullableNumber(value);
  return parsed === null || (parsed !== undefined && parsed >= minimum && parsed <= maximum) ? parsed : undefined;
}

function positiveNumberList(value: unknown): number[] | undefined {
  if (!Array.isArray(value)) return undefined;
  return [...new Set(value.filter((item): item is number => typeof item === 'number' && Number.isFinite(item) && item > 0))];
}

function equipmentPerformanceProfiles(value: unknown): EquipmentPerformanceProfile[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const profiles = value.flatMap((candidate): EquipmentPerformanceProfile[] => {
    const profile = record(candidate);
    const source = optionalString(profile?.source);
    const channelWidthMHz = profile?.channelWidthMHz;
    if (!profile || source === undefined || typeof channelWidthMHz !== 'number' || !Number.isFinite(channelWidthMHz) || channelWidthMHz <= 0 || !Array.isArray(profile.entries)) return [];
    const entries = profile.entries.flatMap((candidateEntry) => {
      const entry = record(candidateEntry);
      if (!entry) return [];
      const name = optionalString(entry.name);
      const modulation = optionalString(entry.modulation);
      const sensitivityDbm = entry.sensitivityDbm;
      const minimumSnrDb = entry.minimumSnrDb;
      const phyRateMbps = entry.phyRateMbps;
      const usableThroughputMbps = entry.usableThroughputMbps;
      if (name === undefined || modulation === undefined
        || typeof sensitivityDbm !== 'number' || !Number.isFinite(sensitivityDbm)
        || typeof minimumSnrDb !== 'number' || !Number.isFinite(minimumSnrDb)
        || typeof phyRateMbps !== 'number' || !Number.isFinite(phyRateMbps) || phyRateMbps < 0
        || typeof usableThroughputMbps !== 'number' || !Number.isFinite(usableThroughputMbps) || usableThroughputMbps < 0) return [];
      return [{ name, modulation, sensitivityDbm, minimumSnrDb, phyRateMbps, usableThroughputMbps }];
    });
    return [{ source, channelWidthMHz, entries }];
  });
  return profiles.length > 0 || value.length === 0 ? profiles : undefined;
}

function equipmentSnapshot(value: unknown): Partial<SiteEquipmentSnapshot> {
  const source = record(value);
  if (!source) return {};
  const snapshot: Partial<SiteEquipmentSnapshot> = {};
  const radioDeviceId = optionalString(source.radioDeviceId);
  // The analysis API enforces the same absolute conducted-power ceiling. Equipment-specific
  // limits are normally lower; rejecting corrupt snapshots here prevents them becoming trusted.
  const radioMaxTxPowerDbm = optionalNullableBoundedNumber(source.radioMaxTxPowerDbm, 0, 60);
  const radioChannelWidthsMHz = positiveNumberList(source.radioChannelWidthsMHz);
  const radioReceiverSensitivityDbm = optionalNullableNumber(source.radioReceiverSensitivityDbm);
  const radioMaxThroughputMbps = optionalNullableNumber(source.radioMaxThroughputMbps);
  const radioMaxRangeKm = optionalNullableBoundedNumber(source.radioMaxRangeKm, 0.001, 10_000);
  const radioPerformanceProfiles = equipmentPerformanceProfiles(source.radioPerformanceProfiles);
  const antennaModelId = optionalNullableString(source.antennaModelId);
  const antennaName = optionalNullableString(source.antennaName);
  if (radioDeviceId !== undefined) snapshot.radioDeviceId = radioDeviceId;
  if (radioMaxTxPowerDbm !== undefined) snapshot.radioMaxTxPowerDbm = radioMaxTxPowerDbm;
  if (radioChannelWidthsMHz !== undefined) snapshot.radioChannelWidthsMHz = radioChannelWidthsMHz;
  if (radioReceiverSensitivityDbm !== undefined) snapshot.radioReceiverSensitivityDbm = radioReceiverSensitivityDbm;
  if (radioMaxThroughputMbps !== undefined) snapshot.radioMaxThroughputMbps = radioMaxThroughputMbps;
  if (radioMaxRangeKm !== undefined) snapshot.radioMaxRangeKm = radioMaxRangeKm;
  if (source.radioPolarization === 'horizontal' || source.radioPolarization === 'vertical' || source.radioPolarization === 'dual' || source.radioPolarization === 'circular') snapshot.radioPolarization = source.radioPolarization;
  if (radioPerformanceProfiles !== undefined) snapshot.radioPerformanceProfiles = radioPerformanceProfiles;
  if (antennaModelId !== undefined) snapshot.antennaModelId = antennaModelId;
  if (antennaName !== undefined) snapshot.antennaName = antennaName;
  return snapshot;
}

function hydrateDisplaySettings(objects: ProjectObjectRecord[]): DisplaySettings {
  const source = objects.find((object) => object.properties.workspaceSettings)?.properties.workspaceSettings;
  if (typeof source !== 'object' || source === null) return defaultDisplaySettings;
  const input = source as Record<string, unknown>;
  const mapStyle = input.mapStyle === 'buildings' || input.mapStyle === 'terrain' || input.mapStyle === 'satellite' || input.mapStyle === 'hybrid'
    ? input.mapStyle
    : defaultDisplaySettings.mapStyle;
  return {
    showCoverage: input.showCoverage !== false,
    showAllAntennas: input.showAllAntennas === true,
    showWeakCoverage: input.showWeakCoverage !== false,
    defaultStationHeightM: Math.max(1, finiteNumber(input.defaultStationHeightM, defaultDisplaySettings.defaultStationHeightM)),
    coverageAccuracy: Math.max(10, Math.min(100, finiteNumber(input.coverageAccuracy, defaultDisplaySettings.coverageAccuracy))),
    coverageOpacity: Math.max(0, Math.min(100, finiteNumber(input.coverageOpacity, defaultDisplaySettings.coverageOpacity))),
    lidarEnabled: input.lidarEnabled !== false,
    lidarCoverage: input.lidarCoverage === true,
    lidarOutlierPercent: Math.max(0, Math.min(2, finiteNumber(input.lidarOutlierPercent, defaultDisplaySettings.lidarOutlierPercent))),
    showAllStations: input.showAllStations !== false,
    rainfallMap: input.rainfallMap === true,
    weatherRadars: input.weatherRadars === true,
    units: input.units === 'imperial' ? 'imperial' : 'metric',
    defaultProductSelection: selectionMode(input.defaultProductSelection),
    mapStyle,
  };
}

export function serializeProjectWorkspace(sites: DesignSite[], obstacles: ManualObstacle[], radioLinks: RadioLinkModel[], ptmpGroups: PtMpGroupModel[], displaySettings: DisplaySettings): ProjectWorkspaceObject[] {
  const siteObjects = sites.map((site): ProjectWorkspaceObject => ({
    objectType: site.type,
    name: site.name,
    geometry: { type: 'Point', coordinates: [site.coordinates.lon, site.coordinates.lat] },
    properties: {
      clientId: site.id,
      groundElevationM: site.groundElevationM,
      antennaHeightM: site.antennaHeightM,
      equipmentName: site.equipmentName,
      frequencyGHz: site.frequencyGHz,
      channelWidthMHz: site.channelWidthMHz,
      txPowerDbm: persistedTxPowerDbm(site.txPowerDbm),
      antennaGainDbi: site.antennaGainDbi,
      cableLossDb: site.cableLossDb,
      noiseFigureDb: site.noiseFigureDb,
      ambientNoiseFloorDbm: site.ambientNoiseFloorDbm,
      ...equipmentSnapshot(site),
    },
  }));
  const obstacleObjects = obstacles.map((obstacle): ProjectWorkspaceObject => ({
    objectType: 'obstacle',
    name: obstacle.name,
    geometry: { type: 'Point', coordinates: [obstacle.coordinates.lon, obstacle.coordinates.lat] },
    properties: { clientId: obstacle.id, kind: obstacle.kind, heightM: obstacle.heightM, estimated: false },
  }));
  const linkObjects = radioLinks.flatMap((link): ProjectWorkspaceObject[] => {
    if (link.kind !== 'ptp') return [];
    const start = sites.find((site) => site.id === link.startSiteId);
    const end = sites.find((site) => site.id === link.endSiteId);
    return start && end ? [{
      objectType: 'ptp_link',
      name: link.name,
      geometry: { type: 'LineString', coordinates: [[start.coordinates.lon, start.coordinates.lat], [end.coordinates.lon, end.coordinates.lat]] },
      properties: { clientId: link.id, startSiteId: start.id, endSiteId: end.id, productSelectionMode: link.productSelectionMode, additionalLossDb: normalizeExplicitLinkLossDb(link.additionalLossDb) },
    }] : [];
  });
  const groupObjects = ptmpGroups.flatMap((group): ProjectWorkspaceObject[] => {
    const ap = sites.find((site) => site.id === group.apSiteId);
    const stations = group.stationSiteIds.flatMap((id) => {
      const station = sites.find((site) => site.id === id);
      return station ? [station] : [];
    });
    if (!ap || stations.length === 0) return [];
    const coordinates = [ap, ...stations].map((site) => [site.coordinates.lon, site.coordinates.lat]);
    return [{
      objectType: 'ptmp_sector',
      name: group.name,
      geometry: { type: 'LineString', coordinates },
      properties: {
        clientId: group.id,
        apSiteId: group.apSiteId,
        stationSiteIds: stations.map((station) => station.id),
        selectedStationId: group.selectedStationId,
        productSelectionMode: group.productSelectionMode,
      },
    }];
  });
  const workspaceObjects = [...siteObjects, ...obstacleObjects, ...linkObjects, ...groupObjects];
  return workspaceObjects.map((object, index) => index === 0
    ? { ...object, properties: { ...object.properties, workspaceSettings: { ...displaySettings } } }
    : object);
}

export function hydrateProjectWorkspace(objects: ProjectObjectRecord[], options: HydrateProjectWorkspaceOptions = {}): HydratedProjectWorkspace {
  const siteTypes = new Set<SiteType>(['site', 'base_station', 'client']);
  const sites = objects.flatMap((object): DesignSite[] => {
    if (!siteTypes.has(object.objectType as SiteType)) return [];
    const coordinates = pointCoordinates(object.geometry);
    if (!coordinates) return [];
    const properties = object.properties;
    return [{
      id: String(properties.clientId ?? object.id),
      name: object.name,
      type: object.objectType as SiteType,
      coordinates,
      groundElevationM: properties.groundElevationM === null || properties.groundElevationM === undefined ? null : finiteNumber(properties.groundElevationM, 0),
      antennaHeightM: finiteNumber(properties.antennaHeightM, object.objectType === 'base_station' ? 24 : 12),
      equipmentName: String(properties.equipmentName ?? (object.objectType === 'base_station' ? 'Freedom Sector 5' : 'Freedom Beam 5')),
      frequencyGHz: finiteNumber(properties.frequencyGHz, 5.8),
      channelWidthMHz: finiteNumber(properties.channelWidthMHz, 40),
      txPowerDbm: persistedTxPowerDbm(properties.txPowerDbm),
      antennaGainDbi: finiteNumber(properties.antennaGainDbi, object.objectType === 'base_station' ? 17 : 23),
      cableLossDb: finiteNumber(properties.cableLossDb, 1),
      noiseFigureDb: finiteNumber(properties.noiseFigureDb, 6),
      ambientNoiseFloorDbm: finiteNumber(properties.ambientNoiseFloorDbm, -95),
      ...equipmentSnapshot(properties),
    }];
  });
  const obstacleKinds = new Set<ObstacleKind>(['building', 'vegetation', 'tower', 'tree', 'manual']);
  const obstacles = objects.flatMap((object): ManualObstacle[] => {
    if (object.objectType !== 'obstacle') return [];
    const coordinates = pointCoordinates(object.geometry);
    if (!coordinates) return [];
    const rawKind = String(object.properties.kind ?? 'manual') as ObstacleKind;
    return [{ id: String(object.properties.clientId ?? object.id), name: object.name, kind: obstacleKinds.has(rawKind) ? rawKind : 'manual', coordinates, heightM: finiteNumber(object.properties.heightM, 0) }];
  }).filter((obstacle) => obstacle.heightM >= 0);
  const knownSiteIds = new Set(sites.map((site) => site.id));
  const radioLinks = objects.flatMap((object): RadioLinkModel[] => {
    if (object.objectType !== 'ptp_link') return [];
    const startSiteId = String(object.properties.startSiteId ?? '');
    const endSiteId = String(object.properties.endSiteId ?? '');
    if (!knownSiteIds.has(startSiteId) || !knownSiteIds.has(endSiteId) || startSiteId === endSiteId) return [];
    return [{ id: String(object.properties.clientId ?? object.id), name: object.name, kind: 'ptp', startSiteId, endSiteId, groupId: null, stationNumber: null, productSelectionMode: selectionMode(object.properties.productSelectionMode), additionalLossDb: normalizeExplicitLinkLossDb(object.properties.additionalLossDb) }];
  });
  const ptmpGroups = objects.flatMap((object): PtMpGroupModel[] => {
    if (object.objectType !== 'ptmp_sector') return [];
    const apSiteId = String(object.properties.apSiteId ?? '');
    const stationSiteIds = stringList(object.properties.stationSiteIds).filter((id) => knownSiteIds.has(id) && id !== apSiteId);
    if (!knownSiteIds.has(apSiteId) || stationSiteIds.length === 0) return [];
    const selectedStationId = String(object.properties.selectedStationId ?? '');
    return [{ id: String(object.properties.clientId ?? object.id), name: object.name, apSiteId, stationSiteIds, selectedStationId: stationSiteIds.includes(selectedStationId) ? selectedStationId : stationSiteIds[0] ?? null, productSelectionMode: selectionMode(object.properties.productSelectionMode) }];
  });
  for (const group of ptmpGroups) {
    const startSiteName = sites.find((site) => site.id === group.apSiteId)?.name ?? group.name;
    group.stationSiteIds.forEach((endSiteId, index) => {
      const endSiteName = sites.find((site) => site.id === endSiteId)?.name ?? `Station ${index + 1}`;
      radioLinks.push({ id: `${group.id}:station:${index + 1}`, name: `${startSiteName} → ${endSiteName}`, kind: 'ptmp', startSiteId: group.apSiteId, endSiteId, groupId: group.id, stationNumber: index + 1, productSelectionMode: group.productSelectionMode, additionalLossDb: 0 });
    });
  }
  const link = radioLinks[0];
  const startSiteId = link?.startSiteId ?? '';
  const endSiteId = link?.endSiteId ?? '';
  const inferLegacyLink = options.inferLegacyLink === true;
  const linkEndpointIds: [string, string] | null = knownSiteIds.has(startSiteId) && knownSiteIds.has(endSiteId) && startSiteId !== endSiteId
    ? [startSiteId, endSiteId]
    : inferLegacyLink && sites.length >= 2 ? [sites[0]!.id, sites[1]!.id] : null;
  if (inferLegacyLink && radioLinks.length === 0 && linkEndpointIds) radioLinks.push({ id: 'legacy-link', name: `${sites[0]?.name ?? 'A'} — ${sites[1]?.name ?? 'B'}`, kind: 'ptp', startSiteId: linkEndpointIds[0], endSiteId: linkEndpointIds[1], groupId: null, stationNumber: null, productSelectionMode: 'auto', additionalLossDb: 0 });
  return { sites, obstacles, linkEndpointIds, radioLinks, ptmpGroups, displaySettings: hydrateDisplaySettings(objects) };
}
